import React, { useState } from "react";
import { MapPin, Mail, Clock, Phone, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { submitAppointment } from "../data/mock";
import { useClinic } from "../context/ClinicContext";

const Appointments = () => {
  const { clinicInfo } = useClinic();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    reason: "",
    preferredTime: "",
    preferredDay: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Mock submission
      const result = await submitAppointment(formData);

      toast({
        title: "Appointment Request Sent!",
        description: result.message,
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        reason: "",
        preferredTime: "",
        preferredDay: "",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="appointments" className="section-padding bg-[#FAFAF9]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-20 px-2 sm:px-0">
          <div className="divider-accent mx-auto"></div>
          <h2 className="heading-1 text-[#1C1917] mb-4 sm:mb-6 mt-6">
            Book Your Appointment
          </h2>
          <p className="body-large text-[#57534E] text-sm sm:text-base">
            Conveniently book your preference online and we will contact you to
            confirm a time and date.
          </p>
          <p className="body-regular text-[#78716C] mt-3 sm:mt-4 text-xs sm:text-sm">
            For enquiries, please call us on{" "}
            <a
              href={`tel:${clinicInfo.phone}`}
              className="text-[#0F766E] hover:text-[#B45309] font-medium transition-colors duration-300 whitespace-nowrap"
            >
              {clinicInfo.phone}
            </a>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 max-w-6xl mx-auto">
          {/* Appointment Form */}
          <div className="bg-[#FFFFFF] p-6 sm:p-8 lg:p-12 rounded-lg shadow-elevated border border-[#E7E5E4]">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-7">
              {/* Name */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="name"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Full Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  required
                  className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="email"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="phone"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  required
                  className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm"
                  placeholder="+968 XXXX XXXX"
                />
              </div>

              {/* Appointment Reason */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="reason"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Appointment Reason *
                </Label>
                <Select
                  value={formData.reason}
                  onValueChange={(value) => handleInputChange("reason", value)}
                  required
                >
                  <SelectTrigger className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultation">
                      Consultation / Check-up
                    </SelectItem>
                    <SelectItem value="toothache">Toothache</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="cosmetic">Cosmetic Dentistry</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Time */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="time"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Preferred Time *
                </Label>
                <Select
                  value={formData.preferredTime}
                  onValueChange={(value) =>
                    handleInputChange("preferredTime", value)
                  }
                  required
                >
                  <SelectTrigger className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning</SelectItem>
                    <SelectItem value="afternoon">Afternoon</SelectItem>
                    <SelectItem value="anytime">Any Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Day */}
              <div className="space-y-2 sm:space-y-3">
                <Label
                  htmlFor="day"
                  className="body-regular text-[#1C1917] font-medium text-xs sm:text-sm"
                >
                  Preferred Day *
                </Label>
                <Select
                  value={formData.preferredDay}
                  onValueChange={(value) =>
                    handleInputChange("preferredDay", value)
                  }
                  required
                >
                  <SelectTrigger className="border-[#D6D3D1] focus:border-[#0F766E] bg-[#FAFAF9] rounded-md h-10 sm:h-12 text-sm">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sun">Sunday</SelectItem>
                    <SelectItem value="mon">Monday</SelectItem>
                    <SelectItem value="tue">Tuesday</SelectItem>
                    <SelectItem value="wed">Wednesday</SelectItem>
                    <SelectItem value="thu">Thursday</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full group mt-6 sm:mt-8 text-xs sm:text-sm px-3 sm:px-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Appointment Request"}
                <Send
                  className="ml-2 w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Button>
            </form>
          </div>

          {/* Clinic Information */}
          <div className="space-y-8 sm:space-y-10 px-2 sm:px-0">
            {/* Location */}
            <div className="flex items-start gap-3 sm:gap-5">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                <MapPin
                  className="w-5 h-5 sm:w-6 sm:h-6 text-[#0F766E]"
                  strokeWidth={1.5}
                />
              </div>
              <div>
                <h3 className="heading-3 text-[#1C1917] mb-2 sm:mb-3 text-sm sm:text-base">
                  Our Location
                </h3>
                <p className="body-regular text-[#57534E] text-xs sm:text-sm">
                  {clinicInfo.address.street}
                  <br />
                  {clinicInfo.address.area}
                </p>
                <p className="body-small text-[#78716C] mt-2 text-xs">
                  {clinicInfo.address.poBox}
                  <br />
                  {clinicInfo.address.country}
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="flex items-start gap-3 sm:gap-5">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                <Phone
                  className="w-5 h-5 sm:w-6 sm:h-6 text-[#0F766E]"
                  strokeWidth={1.5}
                />
              </div>
              <div>
                <h3 className="heading-3 text-[#1C1917] mb-2 sm:mb-3 text-sm sm:text-base">
                  Phone
                </h3>
                <a
                  href={`tel:${clinicInfo.phone}`}
                  className="text-lg sm:text-xl font-light text-[#1C1917] hover:text-[#0F766E] transition-colors duration-300 break-all"
                  style={{
                    fontFamily: "Cormorant, Georgia, serif",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {clinicInfo.phone}
                </a>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-3 sm:gap-5">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F5F5F4] flex items-center justify-center flex-shrink-0">
                <Clock
                  className="w-5 h-5 sm:w-6 sm:h-6 text-[#0F766E]"
                  strokeWidth={1.5}
                />
              </div>
              <div>
                <h3 className="heading-3 text-[#1C1917] mb-2 sm:mb-3 text-sm sm:text-base">
                  Office Hours
                </h3>
                <p className="body-regular text-[#57534E] text-xs sm:text-sm">
                  {clinicInfo.hours.weekdays}
                  <br />
                  {clinicInfo.hours.time}
                </p>
              </div>
            </div>

            {/* Google Maps */}
            <div className="pt-6">
              <a
                href={clinicInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center"
              >
                View on Google Maps
              </a>
            </div>

            {/* Map Embed */}
            <div className="overflow-hidden border border-[#E7E5E4] rounded-lg shadow-refined">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3656.0744842677495!2d58.453267!3d23.597409!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xce8b3ee620c6ab1!2sThe%20Dental%20%26%20Oral%20Center!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${clinicInfo.name} Location`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Appointments;
